import { RecurrenceDaysOfTheWeek } from '@tableau/self-service-schedules/lib/src/components/Recurrence';
import { TimeHhMm } from '@tableau/self-service-schedules/lib/src/components/TimeHhMm';

import { Formatters, Messages } from '~src/codegen/Localize';
import { L10n } from '~src/ts/L10n/L10n';
import { CachingPolicyType, IWorkbookDataFreshnessDescription } from '~src/ts/models/IDataFreshnessInfo';

import { DataFreshnessPolicyOption, DataFreshnessPolicyState, DataFreshnessRecurrenceType, LAST_MONTH_DAY, MINUTES_PER_DAY, MINUTES_PER_HOUR, MINUTES_PER_WEEK, TimeUnit } from '../types/DataFreshnessTypes';

export function findDataFreshnessSummary(workbookDataFreshness: IWorkbookDataFreshnessDescription): string {
  if (workbookDataFreshness === undefined || workbookDataFreshness === null) {
    return Messages.WorkbookDataFreshnessPolicy_NotApplicable();
  }

  if(workbookDataFreshness.error !== undefined){
    return Messages.WorkbookDataFreshnessPolicy_Fetch_Error();
  }

  switch (workbookDataFreshness.cachingPolicyType) {
    case CachingPolicyType.INTERVAL_IN_MINUTES:
      return findRefreshAfterSummary();
    case CachingPolicyType.REPEAT_DAILY:
    case CachingPolicyType.REPEAT_WEEKLY:
    case CachingPolicyType.REPEAT_MONTHLY:
      return findRefreshAtSummary();
    case undefined:
      return Messages.WorkbookDataFreshnessPolicy_SiteDefault(
        {'DataFreshnessInHour': (workbookDataFreshness.defaultCacheTimeoutInMinutes / 60).toString()});
  }

  function findRefreshAtSummary() {
    return Messages.WorkbookDataFreshnessPolicy_FreshAt({'RECURRENCE_SUMMARY':
      findRecurrenceSummary(workbookDataFreshness)});
  }

  function findRecurrenceSummary(workbookDataFreshness: IWorkbookDataFreshnessDescription): string {
    if (workbookDataFreshness === undefined || workbookDataFreshness === null) {
      return undefined;
    }

    return findDataFreshnessRecurrenceSummary(workbookDataFreshness.cachingPolicyType,
      workbookDataFreshness.refreshTimeOfDay, workbookDataFreshness.daysStringCsv);
  }

  function findRefreshAfterSummary() {
    if (workbookDataFreshness.intervalInMinutes === 0) {
      return Messages.WorkbookDataFreshnessPolicy_Disabled();
    }

    const [freshEveryValue, freshEveryTimeUnit] = findProperRefreshAfterWithUnit(
      workbookDataFreshness.defaultCacheTimeoutInMinutes, workbookDataFreshness.intervalInMinutes);
    return Messages.WorkbookDataFreshnessPolicy_FreshEvery(
      {
        'DataFreshnessRefreshEvery': toLocalizedDuration(freshEveryValue, freshEveryTimeUnit)
      });
  }

  function toLocalizedDuration(value: number, timeUnit: TimeUnit) {
    switch (timeUnit) {
      case TimeUnit.Minutes:
        return Messages.minutes({ MINUTES: value, MINUTES_LOC: Formatters.formatCount(value) });
      case TimeUnit.Hours:
        return Messages.hours({ HOURS: value, HOURS_LOC: Formatters.formatCount(value) });
      case TimeUnit.Days:
        return Messages.days({ DAYS: value, DAYS_LOC: Formatters.formatCount(value) });
      case TimeUnit.Weeks:
        return Messages.weeks({ WEEKS: value, WEEKS_LOC: Formatters.formatCount(value) });
    }
  }
}

export function dataFreshnessPolicyTimeZone(workbookDataFreshness: IWorkbookDataFreshnessDescription) {
  return Messages.workbookDataFreshnessPolicy_CurrentTimezone(
    {TIME_ZONE: workbookDataFreshness.timezone && workbookDataFreshness.timezone.displayName || '',
    TIME_ZONE_TYPE: Messages.workbookDataFreshnessPolicy_Policy_Timezone()});
}

export function findProperRefreshAfterWithUnit(defaultCacheTimeoutInMinutes: number, intervalInMinutes: number): [number, TimeUnit] {
  const defaultCacheTimeOutInHour = defaultCacheTimeoutInMinutes && defaultCacheTimeoutInMinutes / MINUTES_PER_HOUR || 12;
  if (intervalInMinutes === undefined) {
    return [defaultCacheTimeOutInHour, TimeUnit.Hours];
  } else if (intervalInMinutes === 0) { // always live
    return [defaultCacheTimeOutInHour, TimeUnit.Hours];
  }

  if (intervalInMinutes % MINUTES_PER_WEEK === 0) {
    return [intervalInMinutes / MINUTES_PER_WEEK, TimeUnit.Weeks];
  } else if (intervalInMinutes % MINUTES_PER_DAY === 0) {
    return [intervalInMinutes / MINUTES_PER_DAY, TimeUnit.Days];
  } else if (intervalInMinutes % MINUTES_PER_HOUR === 0) {
    return [intervalInMinutes / MINUTES_PER_HOUR, TimeUnit.Hours];
  } else {
    return [intervalInMinutes, TimeUnit.Minutes];
  }
}

export function findIntervalInMinutes(dialogState: DataFreshnessPolicyState): number {
  if (dialogState.selectedDataFreshnessPolicy === DataFreshnessPolicyOption.AlwaysLive) {
    return 0;
  } else if (dialogState.selectedDataFreshnessPolicy !== DataFreshnessPolicyOption.FreshAfter) {
    return undefined;
  }

  switch (dialogState.refreshAfterTimeUnit) {
    case TimeUnit.Minutes:
      return dialogState.refreshAfterValue;
    case TimeUnit.Hours:
      return dialogState.refreshAfterValue * MINUTES_PER_HOUR;
    case TimeUnit.Days:
      return dialogState.refreshAfterValue * MINUTES_PER_DAY;
    case TimeUnit.Weeks:
      return dialogState.refreshAfterValue * MINUTES_PER_WEEK;
    default:
      return undefined;
  }
}

export function findCachingPolicyType(dialogState: DataFreshnessPolicyState): string {
  const selectedPolicyType = dialogState.selectedDataFreshnessPolicy;
  if (selectedPolicyType === DataFreshnessPolicyOption.Default) {
    return undefined;
  } else if (selectedPolicyType === DataFreshnessPolicyOption.FreshAfter ||
    selectedPolicyType === DataFreshnessPolicyOption.AlwaysLive) {
    return CachingPolicyType.INTERVAL_IN_MINUTES;
  } else if (selectedPolicyType === DataFreshnessPolicyOption.FreshAt) {
    switch (dialogState.refreshAtType) {
      case DataFreshnessRecurrenceType.Day:
        return CachingPolicyType.REPEAT_DAILY;
      case DataFreshnessRecurrenceType.Week:
        return CachingPolicyType.REPEAT_WEEKLY;
      case DataFreshnessRecurrenceType.Month:
        return CachingPolicyType.REPEAT_MONTHLY;
      default:
        return undefined;
    }
  }
  return undefined;
}

export function findRefreshTimeOfDay(dialogState: DataFreshnessPolicyState): number {
  const selectedPolicyType = dialogState.selectedDataFreshnessPolicy;
  if (selectedPolicyType !== DataFreshnessPolicyOption.FreshAt) {
    return undefined;
  }

  return dialogState.refreshTimeOfDay;
}

export function getDaysStringCsv(dialogState: DataFreshnessPolicyState): string {
  switch (dialogState.refreshAtType) {
    case DataFreshnessRecurrenceType.Month:
      return dialogState.refreshAtDaysOfMonth.join(',');
    case DataFreshnessRecurrenceType.Week:
      return dialogState.refreshAtDaysOfWeek.map(day => mapToString(day)).join(',');
    default:
      return undefined;
  }
}

/**
 * Maps the recurrent types in CachingPolicyType to DataFreshnessRecurrenceType which is used
 * in the DataFreshnessRecurrenceEditor
 */
export function mapToRurrenceType(recurrentType: CachingPolicyType): DataFreshnessRecurrenceType {
  switch (recurrentType) {
    case CachingPolicyType.REPEAT_DAILY:
      return DataFreshnessRecurrenceType.Day;
    case CachingPolicyType.REPEAT_WEEKLY:
      return DataFreshnessRecurrenceType.Week;
    case CachingPolicyType.REPEAT_MONTHLY:
      return DataFreshnessRecurrenceType.Month;
    default:
      return undefined;
  }
}
/**
 * Maps RecurrenceDaysOfTheWeek to strings which correspond to the strings, expected
 * by updateDataFreshnessPolicy in web client
 */
 export function mapToString(day: RecurrenceDaysOfTheWeek): string {
  switch (day) {
    case RecurrenceDaysOfTheWeek.Sun:
        return 'Sun';
    case RecurrenceDaysOfTheWeek.Mon:
        return 'Mon';
    case RecurrenceDaysOfTheWeek.Tue:
        return 'Tue';
    case RecurrenceDaysOfTheWeek.Wed:
        return 'Wed';
    case RecurrenceDaysOfTheWeek.Thu:
        return 'Thu';
    case RecurrenceDaysOfTheWeek.Fri:
        return 'Fri';
    case RecurrenceDaysOfTheWeek.Sat:
        return 'Sat';
    default:
        return undefined;
  }
}

/**
 * Maps strings to RecurrenceDaysOfTheWeek expected by WeekDayPicker
 */
export function mapToRurrenceWeekDay(day: string): RecurrenceDaysOfTheWeek {
  switch (day) {
    case 'Sun':
      return RecurrenceDaysOfTheWeek.Sun;
    case 'Mon':
      return RecurrenceDaysOfTheWeek.Mon;
    case 'Tue':
      return RecurrenceDaysOfTheWeek.Tue;
    case 'Wed':
      return RecurrenceDaysOfTheWeek.Wed;
    case 'Thu':
      return RecurrenceDaysOfTheWeek.Thu;
    case 'Fri':
      return RecurrenceDaysOfTheWeek.Fri;
    case 'Sat':
      return RecurrenceDaysOfTheWeek.Sat;
    default:
      return undefined;
  }
}

function missingDaysStringCsv(cachingPolicyType: CachingPolicyType, daysStringCsv: string): boolean {
  return (cachingPolicyType === CachingPolicyType.REPEAT_MONTHLY ||
    cachingPolicyType === CachingPolicyType.REPEAT_WEEKLY) && daysStringCsv === undefined;
}

export function findDataFreshnessRecurrenceSummary(cachingPolicyType: CachingPolicyType, refreshTimeOfDay: number, daysStringCsv: string) {
  if (cachingPolicyType === undefined || refreshTimeOfDay === undefined ||
    missingDaysStringCsv(cachingPolicyType, daysStringCsv)) {
    return undefined;
  }

  // 780: minutes after midnight for 13:00
  // 720: minutes after midnight for 12:00
  const normalizedRefreshTimeOfDay = refreshTimeOfDay >= 780 ? refreshTimeOfDay - 720 : refreshTimeOfDay; // the hour field is at most 12
  const AMPMStr = refreshTimeOfDay >= 720 ? Messages.PM() : Messages.AM();
  switch (cachingPolicyType) {
    case CachingPolicyType.REPEAT_DAILY:
      return findDailyRecurrenceSummary();
    case CachingPolicyType.REPEAT_WEEKLY:
      return findWeeklyRecurrenceSummary();
    case CachingPolicyType.REPEAT_MONTHLY:
      return findMonthlyRecurrenceSummary();
    default:
      undefined;
  }

  function findDailyRecurrenceSummary() {
    return Messages.WorkbookDataFreshnessPolicy_Daily(
        {'TIME': TimeHhMm.fromNumber(normalizedRefreshTimeOfDay).toHHMMString(), 'AMPM': AMPMStr});
  }

  function findWeeklyRecurrenceSummary() {
    if (daysStringCsv.length === 0) {
      return Messages.WorkbookDataFreshnessPolicy_Weekly_NoDays(
        {'TIME': TimeHhMm.fromNumber(normalizedRefreshTimeOfDay).toHHMMString(), 'AMPM': AMPMStr});
    }

    const days = daysStringCsv.split(',');

    if (days.length === 1) {
      return Messages.WorkbookDataFreshnessPolicy_Weekly_SingleDay(
          {'TIME': TimeHhMm.fromNumber(normalizedRefreshTimeOfDay).toHHMMString(), 'AMPM': AMPMStr,
          'DAY_OF_WEEK': getDayOfWeekString(daysStringCsv)}
      );
    }

    const numOfDays = days.length;
    return Messages.WorkbookDataFreshnessPolicy_Weekly_MultiDays(
        {'TIME': TimeHhMm.fromNumber(normalizedRefreshTimeOfDay).toHHMMString(), 'AMPM': AMPMStr,
          COUNT: numOfDays , COUNT_LOC: L10n.localizedCount(numOfDays)});
  }

  function getDayOfWeekString(day: string): string {
    switch (day) {
      case 'Sun':
        return Messages.dayNameSun();
      case 'Mon':
        return Messages.dayNameMon();
      case 'Tue':
        return Messages.dayNameTue();
      case 'Wed':
        return Messages.dayNameWed();
      case 'Thu':
        return Messages.dayNameThu();
      case 'Fri':
        return Messages.dayNameFri();
      case 'Sat':
        return Messages.dayNameSat();
      default:
        return undefined;
    }
  }

  function findMonthlyRecurrenceSummary() {
    if (daysStringCsv.length === 0) {
      return Messages.WorkbookDataFreshnessPolicy_Monthly_NoDays(
        {'TIME': TimeHhMm.fromNumber(normalizedRefreshTimeOfDay).toHHMMString(), 'AMPM': AMPMStr});
    }

    const days = daysStringCsv.split(',');

    if (days.length === 1) {
      return days[0] === LAST_MONTH_DAY.toString() ?
        Messages.WorkbookDataFreshnessPolicy_Monthly_LastDay(
          {'TIME': TimeHhMm.fromNumber(normalizedRefreshTimeOfDay).toHHMMString(), 'AMPM': AMPMStr}) :
        Messages.WorkbookDataFreshnessPolicy_Monthly_SingleDay(
          {'TIME': TimeHhMm.fromNumber(normalizedRefreshTimeOfDay).toHHMMString(), 'AMPM': AMPMStr,
          'DAY_OF_MONTH': daysStringCsv});
    }
    const numOfDays = days.length;
    return Messages.WorkbookDataFreshnessPolicy_Monthly_MultiDays(
          {'TIME': TimeHhMm.fromNumber(normalizedRefreshTimeOfDay).toHHMMString(), 'AMPM': AMPMStr,
            COUNT: numOfDays , COUNT_LOC: L10n.localizedCount(numOfDays)});
  }
}
